import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserPlus, Trash2, ShieldCheck, Eye, EyeOff, Crown, Pencil, Save } from "lucide-react";
import { useAuth, useCabang } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

function PasswordInput({ value, onChange, placeholder = "", required = false, minLength, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value} onChange={onChange}
             required={required} minLength={minLength} placeholder={placeholder}
             className="pr-10" data-testid={testid} />
      <button type="button" onClick={() => setShow((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600"
              data-testid={`${testid}-toggle`} tabIndex={-1} aria-label="Lihat sandi">
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function Users() {
  const { user } = useAuth();
  const { cabangList } = useCabang();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("cabang");
  const [cabangId, setCabangId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (user && !["super_admin", "admin"].includes(user.role)) navigate("/dashboard");
  }, [user, navigate]);

  const load = async () => {
    try { const { data } = await api.get("/users"); setRows(data); }
    catch { toast.error("Gagal memuat"); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { email: email.trim().toLowerCase(), name: name.trim(), password, role };
      if (role === "cabang") payload.cabang_id = cabangId;
      if (role === "viewer" && user?.role === "super_admin") payload.parent_admin_id = parentAdminId;
      const { data } = await api.post("/users", payload);
      toast.success(data?.invitation_sent ? "Pengguna dibuat & email undangan terkirim" : "Pengguna dibuat");
      setEmail(""); setName(""); setPassword(""); setCabangId("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    } finally { setSubmitting(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Hapus pengguna ${u.email}?`)) return;
    // Optimistic: hilangkan dari UI langsung
    const backup = rows;
    setRows((prev) => prev.filter((r) => r.id !== u.id));
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Pengguna dihapus");
      await load();
    } catch (err) {
      setRows(backup); // rollback
      toast.error(err?.response?.data?.detail || "Gagal menghapus");
    }
  };

  const cabangName = (id) => cabangList.find((c) => c.id === id)?.name || "—";
  const roleLabel = { super_admin: "Super Admin", admin: "Usaha", cabang: "Cabang", viewer: "Viewer" };
  const admins = rows.filter((r) => r.role === "admin");
  const [parentAdminId, setParentAdminId] = useState("");

  return (
    <div className="space-y-6">
      <PageHeader title="Pengguna" />

      <Card className="p-6 border-slate-200">
        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="nama@domain.com" data-testid="user-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Nama</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Nama Lengkap" data-testid="user-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Sandi</Label>
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)}
                           required minLength={6} placeholder="Minimal 6 karakter" testid="user-password" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Peran</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="cabang">Cabang</SelectItem>
                {user?.role === "super_admin" && <SelectItem value="admin">Usaha (Admin)</SelectItem>}
                {["super_admin", "admin"].includes(user?.role) && <SelectItem value="viewer">Viewer (Read-only)</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">
              {role === "viewer" ? (user?.role === "super_admin" ? "Parent Usaha" : "Cakupan") : "Cabang"}
            </Label>
            {role === "viewer" ? (
              user?.role === "super_admin" ? (
                <Select value={parentAdminId} onValueChange={setParentAdminId}>
                  <SelectTrigger data-testid="user-parent-admin"><SelectValue placeholder="Pilih usaha" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <div className="h-10 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600"
                     data-testid="user-viewer-scope-info">
                  Semua cabang milik Anda
                </div>
              )
            ) : (
              <Select value={cabangId} onValueChange={setCabangId} disabled={role !== "cabang"}>
                <SelectTrigger data-testid="user-cabang"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {cabangList.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="lg:col-span-5">
            <Button type="submit"
                    disabled={submitting || (role === "cabang" && !cabangId) || (role === "viewer" && user?.role === "super_admin" && !parentAdminId)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="user-add-btn">
              <UserPlus className="h-4 w-4 mr-2" /> {submitting ? "Menyimpan..." : "Tambah Pengguna"}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-0 overflow-hidden border-slate-200">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead>Email</TableHead>
              <TableHead>Nama</TableHead>
              <TableHead>Peran</TableHead>
              <TableHead>Cabang</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[130px] text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="users-table">
            {rows.map((u) => (
              <TableRow key={u.id || u.email} data-testid={`user-row-${u.id || u.email}`}>
                <TableCell className="font-medium">{u.email}</TableCell>
                <TableCell className="text-sm">{u.name}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-1 ${
                    u.role === "super_admin" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                    u.role === "admin" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                    "bg-slate-100 text-slate-700 border border-slate-200"
                  }`}>
                    {u.role === "super_admin" ? <Crown className="h-3 w-3" /> :
                     u.role === "admin" ? <ShieldCheck className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    {roleLabel[u.role] || u.role}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-slate-500">{u.cabang_id ? cabangName(u.cabang_id) : "—"}</TableCell>
                <TableCell className="text-xs">
                  <span className={u.is_active === false ? "text-rose-600" : "text-emerald-600"}>
                    {u.is_active === false ? "Nonaktif" : "Aktif"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  {u.role !== "super_admin" && (
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setEditing(u)}
                              data-testid={`user-edit-${u.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(u)}
                              data-testid={`user-delete-${u.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {editing && <EditUserDialog user={editing} onClose={() => setEditing(null)} onSaved={load}
                                   cabangList={cabangList} canPickRole={user?.role === "super_admin"} />}
    </div>
  );
}

function EditUserDialog({ user: target, onClose, onSaved, cabangList, canPickRole }) {
  const [name, setName] = useState(target.name || "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(target.role);
  const [cabangId, setCabangId] = useState(target.cabang_id || "");
  const [isActive, setIsActive] = useState(target.is_active !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name, role, cabang_id: role === "cabang" ? cabangId : null, is_active: isActive };
      if (password) payload.password = password;
      await api.put(`/users/${target.id}`, payload);
      toast.success("Pengguna diperbarui");
      onSaved(); onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Edit Pengguna</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Email</Label>
            <Input value={target.email} disabled />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Nama</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-user-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm text-slate-700">Sandi baru (kosongkan bila tidak diubah)</Label>
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)}
                           placeholder="Minimal 6 karakter" testid="edit-user-password" />
          </div>
          {canPickRole && (
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700">Peran</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="cabang">Cabang</SelectItem>
                  <SelectItem value="admin">Usaha (Admin)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {role === "cabang" && (
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700">Cabang</Label>
              <Select value={cabangId} onValueChange={setCabangId}>
                <SelectTrigger data-testid="edit-user-cabang"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {cabangList.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input type="checkbox" id="is-active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)}
                   data-testid="edit-user-active" />
            <Label htmlFor="is-active" className="text-sm text-slate-700 cursor-pointer">Akun aktif</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="edit-user-save">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Menyimpan..." : "Simpan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
